import type { PortableTextBlock } from "@portabletext/types";
import type {
	AreaCode,
	ResolvedBlock,
	SpotprisenBundle,
	SpotprisenPage,
	SpotprisenPageSeo,
} from "#lib/types/spotprisenContent.js";

const isLandingPage = (d: SpotprisenPage): boolean => !d.area;

export type ResolvedSpotprisenPage = {
	blocks: ResolvedBlock[];
	seo: SpotprisenPageSeo | null;
	/** Non-null only when the resolved area doc has authored `areaInfo`. */
	areaInfo: PortableTextBlock[] | null;
};

/**
 * Pick the right content for the current request. Fallback chains per field:
 *
 *   blocks:    area doc with `blocksOverrideEnabled === true` (lang → nb)
 *              → landing doc (lang → nb) → []
 *
 *   seo:       area doc if it exists at all (lang → nb)
 *              → landing doc (lang → nb) → null
 *
 *   areaInfo:  area doc if it has authored content (lang → nb) → null
 *              (no landing fallback — `areaInfo` is hidden on Standard;
 *              consumers fall back to the hardcoded `_meta.ts` text)
 *
 * Blocks, seo and areaInfo can come from different docs: an editor may
 * author area-specific SEO without overriding the blocks (toggle off).
 * "Landing" means a doc with an empty / missing `area` field.
 */
export const selectSpotprisenDoc = (
	bundle: SpotprisenBundle,
	area: AreaCode | null
): ResolvedSpotprisenPage => {
	const areaDoc = area
		? (bundle.pages.find((d) => d.area === area) ??
			bundle.pagesNb.find((d) => d.area === area) ??
			null)
		: null;
	const landingDoc = bundle.pages.find(isLandingPage) ?? bundle.pagesNb.find(isLandingPage) ?? null;

	const blocksDoc = areaDoc?.blocksOverrideEnabled ? areaDoc : landingDoc;
	const seoDoc = areaDoc ?? landingDoc;

	return {
		blocks: blocksDoc?.blocks ?? [],
		seo: seoDoc?.seo ?? null,
		areaInfo: areaDoc?.areaInfo?.length ? areaDoc.areaInfo : null,
	};
};
